import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { X, Trash2, Star } from "@/lib/icons";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TierInput } from "@/components/rubric/RubricParts";
import { needsValue, isNumeric, gradeValue } from "@/lib/rubric";
import { colorOf, iconOf, shadeFor } from "@/lib/habitColors";
import { useAuth } from "@/context/AuthContext";
import { rememberedMonth } from "@/lib/gridMemory";

const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];
const addDays = (iso, n) => { const d = new Date(iso + "T12:00:00"); d.setDate(d.getDate() + n); return d.toLocaleDateString("en-CA"); };
/** Horizontal scroller that opens parked on the newest (right-most) week and stays there across data/range changes and resizes — unless the person scrolled away.
 *  `jump` = { monday, n }: scroll so that week's column is the left-most visible one (counts as scrolled away). */
function TodayScroller({ rangeDeps, deps = [], jump, children, testid }) {
  const ref = useRef(null);
  const away = useRef(false);
  const park = () => { const el = ref.current; if (el && !away.current) el.scrollLeft = el.scrollWidth; };
  /* eslint-disable react-hooks/exhaustive-deps */
  useLayoutEffect(() => { away.current = false; park(); }, rangeDeps);
  useLayoutEffect(park, deps); // entry saved/removed: stay parked unless the person scrolled away
  useLayoutEffect(() => {
    const el = ref.current;
    const col = jump && el?.querySelector(`[data-week="${jump.monday}"]`);
    if (!col) return;
    away.current = true;
    el.scrollLeft = col.offsetLeft;
  }, [jump]);
  /* eslint-enable react-hooks/exhaustive-deps */
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onScroll = () => { away.current = el.scrollLeft < el.scrollWidth - el.clientWidth - 4; };
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(park) : null;
    ro?.observe(el);
    return () => { el.removeEventListener("scroll", onScroll); ro?.disconnect(); };
  }, []);
  return <div ref={ref} className="relative overflow-x-auto pb-1" data-testid={testid}>{children}</div>;
}

const weekLabel = (w, data) => {
  const month = w.newMonth || w.monday === data.start ? monthShort(w.monday) : "";
  return month || (w.days.includes(data.today) ? "Today" : "");
};

const monthShort = (iso) => new Date(iso + "T12:00:00").toLocaleDateString(undefined, { month: "short" });
const monthLong = (ym) => new Date(ym + "-15T12:00:00").toLocaleDateString(undefined, { month: "long", year: "numeric" });
const NARROW = "(max-width: 639px)";
const rangeOptions = (narrow) => (narrow ? [8, 26] : [26, 52]);
/** Saved choice if offered on this screen, else the nearest offered one; nothing saved → screen default. */
const effectiveWeeks = (saved, narrow) => {
  const opts = rangeOptions(narrow);
  if (opts.includes(saved)) return saved;
  return saved == null ? opts[0] : 26;
};
/** Big boxes: the phone 8-week view fits without scrolling and a thumb lands on one day. */
const sizes = (big) => (big
  ? { cell: "w-4 h-4", gap: "gap-1", label: "h-4 w-4 text-[10px] leading-4", dot: "w-5 h-5" }
  : { cell: "w-3 h-3", gap: "gap-[3px]", label: "h-3 w-3 text-[9px] leading-3", dot: "w-4 h-4" });

function useNarrow() {
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia(NARROW).matches);
  useEffect(() => {
    const mq = window.matchMedia(NARROW);
    const on = (e) => setNarrow(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

/** Months in the current range, newest first: [{ ym, monday (first week whose Monday is in the month) }]. */
function monthsOf(weeks) {
  const seen = new Map();
  weeks.forEach((w) => { const ym = w.monday.slice(0, 7); if (!seen.has(ym)) seen.set(ym, w.monday); });
  return Array.from(seen, ([ym, monday]) => ({ ym, monday })).reverse();
}

/** Days per month (YYYY-MM → count) with at least one entry among `habits`, judged on the days inside the range. */
function dayCounts(habits, weeks) {
  const counts = new Map();
  weeks.forEach((w) => w.days.forEach((iso) => {
    if (habits.some((h) => h.days[iso])) { const ym = iso.slice(0, 7); counts.set(ym, (counts.get(ym) || 0) + 1); }
  }));
  return counts;
}

/** Months (YYYY-MM) with at least one entry. Today's month always counts as filled. */
function filledMonths(habits, weeks, today) {
  return new Set([today.slice(0, 7), ...dayCounts(habits, weeks).keys()]);
}

const monthSuffix = (ym, now, n) => {
  const days = n ? ` · ${n} day${n === 1 ? "" : "s"}` : "";
  if (ym === now) return ` · now${days}`;
  return n ? days : " · empty";
};

function MonthJump({ weeks, today, counts, onJump }) {
  const months = monthsOf(weeks);
  const now = today.slice(0, 7);
  return (
    <select value="" onChange={(e) => e.target.value && onJump(e.target.value)} aria-label="Jump to month" data-testid="grid-month-jump"
      className="text-xs font-semibold text-stone-500 bg-stone-200/70 rounded-full px-3 py-1.5 border-0 focus:ring-2 focus:ring-amber-400 cursor-pointer">
      <option value="">Jump to month…</option>
      {months.map((m) => {
        const n = counts.get(m.ym) || 0;
        const empty = !n && m.ym !== now;
        return <option key={m.ym} value={m.monday} data-empty={empty || undefined} data-days={n} style={empty ? { color: "#a8a29e" } : undefined}>
          {monthLong(m.ym)}{monthSuffix(m.ym, now, n)}</option>;
      })}
    </select>
  );
}

/** Faint wash behind a month's columns when that month is empty (never today's month). */
const washFor = (w, filled, cls) => (filled.has(w.monday.slice(0, 7)) ? "" : cls);

function WeekLabel({ w, data, habitId, onJump, empty }) {
  const text = weekLabel(w, data);
  const isToday = w.days.includes(data.today);
  const cls = `h-4 text-[9px] leading-4 whitespace-nowrap ${empty ? "text-stone-300" : "text-stone-400"}`;
  if (text && text !== "Today") {
    return <button type="button" onClick={() => onJump(w.monday)} data-testid={`grid-month-label-${habitId}-${w.monday}`} data-empty={empty || undefined}
      className={`${cls} text-left cursor-pointer hover:underline active:underline decoration-stone-400 underline-offset-2`}>{text}</button>;
  }
  return <span className={cls} data-testid={isToday ? `grid-today-label-${habitId}` : undefined}>{text}</span>;
}

const buildWeeks = (start, weeks) => Array.from({ length: weeks }, (_, w) => {
  const monday = addDays(start, w * 7);
  const days = Array.from({ length: 7 }, (_, d) => addDays(monday, d));
  return { monday, days, newMonth: w > 0 && monday.slice(0, 7) !== addDays(start, (w - 1) * 7).slice(0, 7) };
});

function useWeeks(start, weeks) {
  return useMemo(() => buildWeeks(start, weeks), [start, weeks]);
}

function GridCard({ h, data, onPick, readOnly, big, jump, onJump }) {
  const weeks = useWeeks(data.start, data.weeks);
  const sz = sizes(big);
  const filled = useMemo(() => filledMonths([h], weeks, data.today), [h, weeks, data.today]);
  const c = colorOf(h);
  const Icon = iconOf(h);
  const canEdit = (iso) => !readOnly && iso <= data.today && (new Date(data.today) - new Date(iso)) / 864e5 <= data.edit_window_days && iso >= h.created;
  const total = Object.keys(h.days).length;
  return (
    <div className="warm-card p-4 sm:p-5" data-testid={`grid-card-${h.id}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-8 h-8 rounded-xl grid place-items-center ${c.chip}`}><Icon className="w-4 h-4" /></span>
          <div className="min-w-0"><div className="font-display font-bold text-stone-900 truncate flex items-center gap-1">{h.focus === "keystone" && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" />}{h.title}</div>
            <div className="text-[11px] text-stone-500">{total} day{total === 1 ? "" : "s"} in view{readOnly ? "" : ` · tap a box to add or fix an entry (last ${data.edit_window_days} days)`}</div></div>
        </div>
      </div>
      <TodayScroller rangeDeps={[data.start, data.weeks]} deps={[total]} jump={jump} testid={`grid-scroll-${h.id}`}>
        <div className={`flex ${sz.gap} items-end min-w-max`}>
          <div className={`flex flex-col ${sz.gap} mr-1 pt-4`}>{DAY_LABELS.map((d, i) => <span key={i} className={`${sz.label} text-stone-400 text-center`}>{d}</span>)}</div>
          {weeks.map((w) => (
            <div key={w.monday} data-week={w.monday} data-washed={filled.has(w.monday.slice(0, 7)) ? undefined : true}
              className={`flex flex-col ${sz.gap} rounded-[4px] ${washFor(w, filled, "bg-stone-100")} ${w.newMonth ? "border-l border-stone-300 pl-1.5 ml-0.5" : ""}`}>
              <WeekLabel w={w} data={data} habitId={h.id} onJump={onJump} empty={!filled.has(w.monday.slice(0, 7))} />
              {w.days.map((iso) => {
                const e = h.days[iso];
                const future = iso > data.today;
                const editable = canEdit(iso);
                return (
                  <button key={iso} type="button" disabled={future || (!editable && !e)} onClick={() => editable && onPick(h, iso, e)} title={`${iso}${e ? ` · ${e.tier}${e.value != null ? ` · ${e.value}` : ""}` : ""}`}
                    data-testid={`grid-cell-${h.id}-${iso}`}
                    className={`${sz.cell} rounded-[3px] transition ${future ? "bg-transparent" : e ? shadeFor(h, e.tier) : iso === data.today ? "bg-stone-300 ring-1 ring-stone-400" : "bg-stone-200/70"} ${editable ? "hover:scale-125 hover:ring-2 hover:ring-stone-400 cursor-pointer" : "cursor-default"}`} />
                );
              })}
            </div>
          ))}
        </div>
      </TodayScroller>
      <div className="flex gap-3 mt-2 text-[10px] text-stone-500">
        {h.rubric.tiers.map((t) => [t.label, { full: 3, partial: 2, small: 1, showed_up: 0 }[t.key]]).map(([l, i]) => <span key={l} className="inline-flex items-center gap-1"><span className={`w-2.5 h-2.5 rounded-sm ${c.shades[i]}`} />{l}</span>)}
      </div>
    </div>
  );
}

function Overview({ data, big, jump }) {
  const weeks = useWeeks(data.start, data.weeks);
  const hs = data.habits;
  const sz = sizes(big);
  const filled = useMemo(() => filledMonths(hs, weeks, data.today), [hs, weeks, data.today]);
  return (
    <div className="warm-card p-4 sm:p-5" data-testid="grid-overview">
      <div className="font-display font-bold text-stone-900 mb-1">All habits</div>
      <div className="text-[11px] text-stone-500 mb-3">One dot per habit, per day. {hs.map((h) => <span key={h.id} className="inline-flex items-center gap-1 mr-2"><span className={`w-2 h-2 rounded-full ${colorOf(h).dot}`} />{h.title}</span>)}</div>
      <TodayScroller rangeDeps={[data.start, data.weeks]} deps={[hs.length]} jump={jump} testid="grid-scroll-overview">
        <div className={`flex ${sz.gap} min-w-max`}>
          {weeks.map((w) => (
            <div key={w.monday} data-week={w.monday} data-washed={filled.has(w.monday.slice(0, 7)) ? undefined : true}
              className={`flex flex-col ${sz.gap} rounded-[4px] ${washFor(w, filled, "bg-stone-200/30")} ${w.newMonth ? "border-l border-stone-300 pl-1.5 ml-0.5" : ""}`}>
              <span className="h-4 text-[9px] leading-4 text-stone-400 whitespace-nowrap" data-testid={w.days.includes(data.today) ? "grid-today-label-overview" : undefined}>{w.days.includes(data.today) ? "Today" : ""}</span>
              {w.days.map((iso) => (
                <div key={iso} className={`${sz.dot} rounded-[3px] ${iso > data.today ? "" : "bg-stone-100"} grid grid-cols-2 gap-px p-px`} title={iso}>
                  {hs.slice(0, 4).map((h) => <span key={h.id} className={`rounded-[1px] ${h.days[iso] ? shadeFor(h, h.days[iso].tier) : ""}`} />)}
                </div>
              ))}
            </div>
          ))}
        </div>
      </TodayScroller>
    </div>
  );
}

function EditEntry({ pick, onClose, onSaved }) {
  const { h, iso, entry } = pick;
  const measured = needsValue(h.rubric);
  const [value, setValue] = useState(entry?.value ?? "");
  const preview = measured && value !== "" ? gradeValue(h.rubric, isNumeric(h.rubric.type) ? Number(value) : value) : null;
  const save = async () => {
    try {
      const r = await api.put(`/habits/${h.id}/logs/${iso}`, { value: measured ? (isNumeric(h.rubric.type) ? Number(value) : value) : undefined });
      toast.success(`${iso}: ${r.data.tier.label}${r.data.points_delta ? ` (${r.data.points_delta > 0 ? "+" : ""}${r.data.points_delta} pts)` : ""}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
  };
  const remove = async () => {
    const r = await api.delete(`/habits/${h.id}/logs/${iso}`);
    toast(`Removed ${iso}${r.data.points_reversed ? ` (−${r.data.points_reversed} pts)` : ""}.`);
    onSaved();
  };
  const nice = new Date(iso + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="grid-edit-modal">
      <div className="warm-card w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div><div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{h.title}</div><div className="font-display text-lg font-bold text-stone-900">{nice}</div></div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700" data-testid="grid-edit-close"><X className="w-5 h-5" /></button>
        </div>
        {entry ? <p className="text-sm text-stone-600">Logged as <strong>{h.rubric.tiers.find((t) => t.key === entry.tier)?.label || entry.tier}</strong>{entry.value != null ? ` (${entry.value})` : ""}.</p> : <p className="text-sm text-stone-600">Nothing logged that day. Add it if you did the habit — it counts the same as a check-in.</p>}
        {measured && <TierInput rubric={h.rubric} value={value} onChange={setValue} idPrefix="grid-edit" />}
        <div className="flex gap-2 flex-wrap">
          <button onClick={save} disabled={measured && value === ""} data-testid="grid-edit-save" className="amber-btn text-sm py-2 disabled:opacity-40">{entry ? (measured ? `Update${preview ? ` → ${preview.label}` : ""}` : "Keep as done") : (measured ? `Add${preview ? ` → ${preview.label}` : ""}` : "Mark as done")}</button>
          {entry && <button onClick={remove} data-testid="grid-edit-delete" className="inline-flex items-center gap-1 text-sm font-semibold px-4 py-2 rounded-full bg-white border border-stone-300 text-stone-700 hover:bg-red-50 hover:text-red-800"><Trash2 className="w-4 h-4" /> Remove entry</button>}
        </div>
      </div>
    </div>
  );
}

export function HabitGrid({ onChanged, endpoint = "/habits/grid", readOnly = false, title = "Your patterns" }) {
  const narrow = useNarrow();
  const { user } = useAuth();
  const memoryKey = `${user?.id}:${endpoint}`;
  const [saved, setSaved] = useState(undefined); // undefined = not loaded yet, null = never chosen
  const [data, setData] = useState(null);
  const [pick, setPick] = useState(null);
  const [jump, setJump] = useState(null);
  const weeks = saved === undefined ? null : effectiveWeeks(saved, narrow);
  useEffect(() => { api.get("/me/grid-weeks").then((r) => setSaved(r.data.weeks ?? null)).catch(() => setSaved(null)); }, []);
  const load = useCallback(async () => { if (!weeks) return; const r = await api.get(`${endpoint}?weeks=${weeks}`); setData(r.data); }, [weeks, endpoint]);
  useEffect(() => { load(); }, [load]);
  const choose = (w) => { setSaved(w); setJump(null); rememberedMonth.delete(memoryKey); api.patch("/me/grid-weeks", { weeks: w }).catch(() => {}); };
  const onJump = (monday) => { rememberedMonth.set(memoryKey, monday); setJump({ monday, n: Date.now() }); };
  const loaded = !!data;
  useEffect(() => {
    const monday = loaded && rememberedMonth.get(memoryKey);
    if (monday) setJump({ monday, n: Date.now() });
  }, [loaded, memoryKey]);
  if (!data || !weeks) return <div className="text-sm text-stone-500 p-6">Loading your grid…</div>;
  const big = narrow && weeks === 8;
  const allWeeks = buildWeeks(data.start, data.weeks);
  const counts = dayCounts(data.habits, allWeeks);
  return (
    <div className="space-y-4" data-testid="habit-grid">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div><h2 className="font-display text-2xl font-bold text-stone-900">{title}</h2><p className="text-sm text-stone-500">Rows are Mon–Sun, columns are weeks, lines mark a new month. Darker = fuller credit.</p></div>
        <div className="flex items-center gap-2 flex-wrap">
          <MonthJump weeks={allWeeks} today={data.today} counts={counts} onJump={onJump} />
          <div className="flex gap-1 rounded-full bg-stone-200/70 p-1" data-testid="grid-range">{rangeOptions(narrow).map((w) => <button key={w} onClick={() => choose(w)} data-testid={`grid-weeks-${w}`} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${weeks === w ? "bg-stone-900 text-white" : "text-stone-600"}`}>{w} weeks</button>)}</div>
        </div>
      </div>
      {data.habits.length > 1 && <Overview data={data} big={big} jump={jump} />}
      {data.habits.map((h) => <GridCard key={h.id} h={h} data={data} readOnly={readOnly} big={big} jump={jump} onJump={onJump} onPick={(hh, iso, e) => !readOnly && setPick({ h: hh, iso, entry: e })} />)}
      {data.habits.length === 0 && <div className="warm-card p-8 text-center text-stone-500">Add a habit and your grid will start filling in.</div>}
      {pick && <EditEntry pick={pick} onClose={() => setPick(null)} onSaved={() => { setPick(null); load(); onChanged?.(); }} />}
    </div>
  );
}
